import { Handle, Position } from '@xyflow/react';
import { useTranslation } from 'react-i18next';
import { Users, GitBranch, Minus } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { getPaletteColor } from '@/core/lib/colors';
import { useTreeCollapse } from '@/shared/components/custom/tree-chart';
import { OrganizationUnitIcon } from '../OrganizationUnitIconPicker';
import { OrgAreaNodeActions } from './OrgAreaNodeActions';
import type { OrgAreaNodeData } from '../../hooks/useOrgChartData';

interface OrgAreaNodeProps {
  data: OrgAreaNodeData;
}

export function OrgAreaNode({ data }: OrgAreaNodeProps) {
  const { t } = useTranslation('organizationUnits');
  const { areaId, name, color, icon, childAreaCount, hasChildren, memberCount } = data;
  const { collapsedIds, toggleCollapse } = useTreeCollapse();
  const collapsed = collapsedIds.has(areaId);
  const colorValue = getPaletteColor(color ?? '')?.value;

  const directChildCount = childAreaCount + memberCount;

  return (
    <div
      className={cn(
        'group relative flex w-50 flex-col gap-2 rounded-xl border-2 border-l-4 bg-card px-3 py-3 shadow-sm transition-shadow hover:shadow-md',
      )}
      style={{
        borderColor: colorValue ?? 'var(--color-border)',
        pointerEvents: 'all',
      }}
      data-testid={`org-chart-area-node-${areaId}`}
    >
      <Handle type="target" position={Position.Top} className="border-border! bg-muted!" />

      {/* Actions overlay — absolute so name always has full width */}
      <div
        className="absolute right-2 top-2 z-10 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100"
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <OrgAreaNodeActions areaId={areaId} childAreaCount={childAreaCount} />
      </div>

      {/* Header: icon + name */}
      <div className="flex min-w-0 items-center gap-2">
        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
          style={{ backgroundColor: colorValue ? `${colorValue}20` : undefined }}
        >
          <OrganizationUnitIcon
            name={icon ?? 'Building2'}
            className="h-4 w-4"
            style={{ color: colorValue }}
          />
        </div>
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{name}</p>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-3">
        {memberCount > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Users className="h-3 w-3" />
            <span>{memberCount}</span>
          </div>
        )}
        {childAreaCount > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <GitBranch className="h-3 w-3" />
            <span>{t('orgChart.areaNode.subarea', { count: childAreaCount })}</span>
          </div>
        )}
      </div>

      {/* Source handle — invisible when collapse button is shown */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={
          hasChildren
            ? { opacity: 0, pointerEvents: 'none' }
            : { backgroundColor: colorValue ?? undefined, borderColor: colorValue ?? undefined }
        }
      />

      {/* Collapse indicator: "−" when expanded, count when collapsed */}
      {hasChildren && (
        <button
          type="button"
          className="absolute bottom-0 left-1/2 z-20 flex h-5 w-5 -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full text-white shadow-sm"
          style={{ backgroundColor: colorValue ?? 'var(--color-muted-foreground)' }}
          onClick={() => toggleCollapse(areaId)}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          data-testid={`org-chart-area-collapse-${areaId}`}
        >
          {collapsed ? (
            <span className="text-[10px] font-bold leading-none">{directChildCount}</span>
          ) : (
            <Minus className="h-3 w-3" />
          )}
        </button>
      )}
    </div>
  );
}
